import apiClient from '../api/client';

export interface AssayType {
  id: number;
  name: string;
  category: string;
  description: string | null;
  typical_units: string | null;
}

export interface Assay {
  id: number;
  project_id: number | null;
  tenant_id: number;
  name: string;
  assay_type_id: number | null;
  assay_type_name: string | null;
  target_name: string | null;
  target_uniprot: string | null;
  description: string | null;
  protocol: string | null;
  conditions: Record<string, any>;
  plate_format: number | null;
  result_count: number;
  created_at: string | null;
}

export interface AssayResult {
  id: number;
  assay_id: number;
  molecule_id: number | null;
  compound_id: string | null;
  smiles: string | null;
  value: number;
  unit: string | null;
  qualifier: string;
  std_dev: number | null;
  n_replicates: number;
  hill_slope: number | null;
  r_squared: number | null;
  well_id: string | null;
  plate_id: string | null;
  operator: string | null;
  assay_date: string | null;
  notes: string | null;
  molecule_smiles: string | null;
}

export interface BioactivitySummary {
  assay_name: string;
  assay_id: number;
  /** Registry assay type ID used by /biology/assays/:assayId, when linked. */
  registry_assay_id: number | null;
  target_name: string | null;
  assay_type: string | null;
  value: number;
  unit: string | null;
  qualifier: string;
  hill_slope: number | null;
  r_squared: number | null;
  assay_date: string | null;
  has_curve: boolean;
}

export interface PlateData {
  assay_id: number;
  plate_id: string;
  plate_format: number;
  wells: Record<string, any>;
  well_count: number;
  value_range: { min: number; max: number } | null;
  qc: { z_prime: number | null; signal_to_background: number | null; cv_percent: number | null };
}

export interface ImportReport {
  total_rows: number;
  imported: number;
  matched: number;
  unmatched: number;
  skipped: number;
  errors: string[];
  assay_id: number;
  column_mapping: Record<string, string>;
}

export const assayApi = {
  listTypes: async (category?: string): Promise<AssayType[]> => {
    const params = category ? { category } : {};
    const res = await apiClient.get('/v1/assays/types', { params });
    return res.data;
  },

  listAssays: async (params: {
    project_id?: number;
    tenant_id?: number;
    target_name?: string;
    limit?: number;
    offset?: number;
  }): Promise<Assay[]> => {
    const res = await apiClient.get('/v1/assays', { params });
    return res.data;
  },

  getAssay: async (id: number): Promise<Assay> => {
    const res = await apiClient.get(`/v1/assays/${id}`);
    return res.data;
  },

  createAssay: async (data: {
    project_id: number;
    tenant_id?: number;  // server-derived from JWT; not sent by the client
    name: string;
    assay_type?: string;
    target_name?: string;
    description?: string;
    plate_format?: number;
  }): Promise<Assay> => {
    const res = await apiClient.post('/v1/assays', data);
    return res.data;
  },

  listResults: async (assayId: number, limit = 500, offset = 0): Promise<AssayResult[]> => {
    const res = await apiClient.get(`/v1/assays/${assayId}/results`, {
      params: { limit, offset },
    });
    return res.data;
  },

  getPlateData: async (assayId: number, plateId: string): Promise<PlateData> => {
    const res = await apiClient.get(`/v1/assays/${assayId}/plate/${plateId}`);
    return res.data;
  },

  getDoseResponse: async (assayId: number, moleculeId: number) => {
    const res = await apiClient.get(`/v1/assays/${assayId}/dose-response/${moleculeId}`);
    return res.data;
  },

  getMoleculeBioactivity: async (moleculeId: number): Promise<BioactivitySummary[]> => {
    const res = await apiClient.get(`/v1/assays/molecules/${moleculeId}/bioactivity`);
    return res.data;
  },

  importCSV: async (assayId: number, file: File, createPerTarget = false): Promise<ImportReport> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiClient.post(
      `/v1/assays/${assayId}/import?create_assay_per_target=${createPerTarget}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  },

  detectFileType: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiClient.post('/v1/assays/detect-file-type', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
};
